/**
 * LocationSocialMediaService
 * Service for LocationSocialMedia entity operations
 * Route: api/location-social-media
 * Controller: LocationSocialMediaController.cs
 * Note: Uses composite key (MediaID + LocationID)
 * Contains sensitive data (Username, Password, Email) - Admin only for full access
 */
import { CrudService } from '../base/CrudService';
/**
 * LocationSocialMediaService
 * Handles location social media CRUD operations and custom endpoints
 * Maps to /api/location-social-media endpoint
 */
export class LocationSocialMediaService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? LocationSocialMediaService.DEFAULT_SERVICE_PATH,
            basePath: LocationSocialMediaService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get all social media accounts for a specific location (Admin only - full details)
     * GET /api/location-social-media/location/{locationId}
     * @param locationId - Location ID
     * @returns Array of location social media accounts
     */
    async getByLocationId(locationId) {
        const response = await this.getByLocationIdRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get all social media accounts for a specific location (raw)
     * @param locationId - Location ID
     * @returns ResponseData with array of location social media accounts
     */
    getByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('byLocationId', `location/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get public social media information for a location (Handle and FullLink only)
     * GET /api/location-social-media/location/{locationId}/public
     * @param locationId - Location ID
     * @returns Array of public social media information
     */
    async getPublicSocialMedia(locationId) {
        const response = await this.getPublicSocialMediaRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get public social media information for a location (raw)
     * @param locationId - Location ID
     * @returns ResponseData with array of public social media information
     */
    getPublicSocialMediaRaw(locationId) {
        const endpoint = this.getEndpoint('publicSocialMedia', `location/${locationId}/public`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get a location social media by composite key (MediaID + LocationID) - Admin only
     * GET /api/location-social-media/{mediaId}/{locationId}
     * @param mediaId - Media ID
     * @param locationId - Location ID
     * @returns Location social media account
     */
    async getByCompositeKey(mediaId, locationId) {
        const response = await this.getByCompositeKeyRaw(mediaId, locationId);
        if (!response.data) {
            throw new Error(`Location social media (Media: ${mediaId}, Location: ${locationId}) not found`);
        }
        return response.data;
    }
    /**
     * Get a location social media by composite key (raw)
     * @param mediaId - Media ID
     * @param locationId - Location ID
     * @returns ResponseData with location social media account
     */
    getByCompositeKeyRaw(mediaId, locationId) {
        const endpoint = this.getEndpoint('byCompositeKey', `${mediaId}/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Update a location social media account (Admin only)
     * PUT /api/location-social-media/{mediaId}/{locationId}
     * @param mediaId - Media ID
     * @param locationId - Location ID
     * @param locationSocialMedia - Location social media data to update
     * @returns Updated location social media
     */
    async updateByCompositeKey(mediaId, locationId, locationSocialMedia) {
        const response = await this.updateByCompositeKeyRaw(mediaId, locationId, locationSocialMedia);
        if (!response.data) {
            throw new Error(`Failed to update location social media (Media: ${mediaId}, Location: ${locationId})`);
        }
        return response.data;
    }
    /**
     * Update a location social media account (raw)
     * @param mediaId - Media ID
     * @param locationId - Location ID
     * @param locationSocialMedia - Location social media data to update
     * @returns ResponseData with updated location social media
     */
    updateByCompositeKeyRaw(mediaId, locationId, locationSocialMedia) {
        const endpoint = this.getEndpoint('update', `${mediaId}/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, locationSocialMedia);
    }
    /**
     * Delete a location social media account (Admin only)
     * DELETE /api/location-social-media/{mediaId}/{locationId}
     * @param mediaId - Media ID
     * @param locationId - Location ID
     * @returns True if deleted successfully
     */
    async deleteByCompositeKey(mediaId, locationId) {
        const response = await this.deleteByCompositeKeyRaw(mediaId, locationId);
        return response.success;
    }
    /**
     * Delete a location social media account (raw)
     * @param mediaId - Media ID
     * @param locationId - Location ID
     * @returns ResponseData
     */
    deleteByCompositeKeyRaw(mediaId, locationId) {
        const endpoint = this.getEndpoint('delete', `${mediaId}/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
}
LocationSocialMediaService.DEFAULT_SERVICE_PATH = 'locations';
LocationSocialMediaService.BASE_PATH = 'api/location-social-media';
//# sourceMappingURL=LocationSocialMediaService.js.map